import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDateTime } from "@/lib/api";
import { PageHead } from "@/pages/AppLayout";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Bell, ChevronRight, Circle, CheckCircle2, Trash2, CheckCheck, X } from "lucide-react";

export default function Notifications() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);

  const load = () => api.get("/notifications").then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);

  const fire = () => window.dispatchEvent(new Event("notif-read"));

  const setRead = async (n, read) => {
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read } : x)));
    try { await api.patch(`/notifications/${n.id}`, { read }); fire(); }
    catch { load(); }
  };

  const open = (n) => {
    if (!n.read) setRead(n, true);
    if (n.link) nav(n.link);
  };

  const del = async (n) => {
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    try { await api.delete(`/notifications/${n.id}`); fire(); }
    catch { load(); }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    try { await api.post("/notifications/read-all"); fire(); } catch { load(); }
  };

  const clearAll = async () => {
    try { await api.delete("/notifications"); setItems([]); fire(); toast.success("Inbox cleared"); }
    catch { load(); }
  };

  const hasUnread = items && items.some((n) => !n.read);

  return (
    <div className="p-5 md:p-10 pb-24 max-w-3xl" data-testid="notifications-page">
      <PageHead title="Inbox" sub="Updates and messages for your fleet." />

      {items && items.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-4">
          <button onClick={markAllRead} disabled={!hasUnread} className="btn-ghost h-10 px-4 font-head text-xs flex items-center gap-2 disabled:opacity-40" data-testid="mark-all-read-btn">
            <CheckCheck className="w-4 h-4" /> Mark all read
          </button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="btn-ghost h-10 px-4 font-head text-xs flex items-center gap-2 hover:text-[#FF3B30]" data-testid="clear-all-btn"><X className="w-4 h-4" /> Clear all</button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#1A1C23] border border-white/10 text-white">
              <AlertDialogHeader><AlertDialogTitle className="font-head uppercase">Clear all messages?</AlertDialogTitle>
                <AlertDialogDescription className="text-[#94A3B8]">This permanently deletes every message in your inbox. This can&apos;t be undone.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="btn-ghost border-white/20">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={clearAll} className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80" data-testid="confirm-clear-all">Clear all</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {items === null ? (
        <p className="font-head text-[#FFB800] uppercase tracking-widest">Loading…</p>
      ) : items.length === 0 ? (
        <div className="card p-12 text-center">
          <Bell className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
          <p className="font-body text-[#94A3B8]">No messages yet.</p>
        </div>
      ) : (
        <div className="card divide-y divide-white/10">
          {items.map((n) => {
            const clickable = !!n.link;
            return (
              <div key={n.id} className={`flex items-start gap-2 pr-3 ${n.read ? "" : "border-l-4 border-[#FFB800]"}`} data-testid={`notif-${n.id}`}>
                <button
                  type="button"
                  onClick={() => open(n)}
                  className={`flex-1 min-w-0 text-left px-5 py-4 ${clickable ? "hover:bg-[#1A1C23] cursor-pointer" : "cursor-default"} transition-colors`}
                  data-testid={`notif-open-${n.id}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className={`font-head font-700 text-lg uppercase tracking-tight ${n.read ? "text-white/70" : "text-white"}`}>{n.title}</p>
                    <span className="font-mono text-[10px] text-[#94A3B8] shrink-0">{fmtDateTime(n.created_at)}</span>
                  </div>
                  <p className="font-body text-[#94A3B8] mt-1">{n.body}</p>
                  {clickable && <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#FFB800] mt-2">View <ChevronRight className="w-3 h-3" /></span>}
                </button>
                <div className="flex items-center gap-1 pt-4 shrink-0">
                  <button onClick={() => setRead(n, !n.read)} title={n.read ? "Mark unread" : "Mark read"} className="text-[#94A3B8] hover:text-[#FFB800] transition-colors p-1" data-testid={`notif-read-${n.id}`}>
                    {n.read ? <Circle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                  </button>
                  <button onClick={() => del(n)} title="Delete" className="text-[#94A3B8] hover:text-[#FF3B30] transition-colors p-1" data-testid={`notif-delete-${n.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
